import { useState } from "react";
import { FileText, X, Download, ShieldCheck, ExternalLink, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PARTNER_TYPES = [
  { value: "conveyancer", label: "Conveyancer" },
  { value: "real_estate_agent", label: "Real Estate Agent" },
  { value: "buyer_agent", label: "Buyer Agent" },
  { value: "property_manager", label: "Property Manager" },
  { value: "other", label: "Other" },
];

/**
 * Conveyancing Water Certificate™ button + generation modal.
 * Used on /passport/property/:id by property owners.
 */
export function ConveyancingCertButton({ propertyId, certAvailable }) {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [created, setCreated] = useState(null);
  const [shareUrl, setShareUrl] = useState("");

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/conveyancing/certificates", {
        property_id: propertyId,
        apply_white_label: false,
        passport_share_url: shareUrl || null,
        public_base_url: window.location.origin,
      });
      setCreated(data);
      toast.success("Certificate generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate certificate");
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = async (certId, certNumber) => {
    const token = localStorage.getItem("scwt_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conveyancing/certificates/${certId}/download.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `conveyancing-water-certificate-${certNumber}.pdf`;
    a.click();
  };

  return (
    <>
      <button
        data-testid="conveyancing-cert-btn"
        onClick={() => setOpen(true)}
        disabled={!certAvailable}
        title={certAvailable ? "Generate Conveyancing Water Certificate" : "Available after first published water test"}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-cyan-500 hover:text-cyan-700 text-sm font-semibold text-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ShieldCheck className="w-4 h-4" /> Conveyancing Certificate
      </button>

      {open && (
        <div data-testid="conveyancing-cert-modal" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-7 my-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">Conveyancing Water Certificate™</div>
                <h2 className="font-display mt-1 text-2xl font-bold text-slate-900">Generate certificate</h2>
                <p className="text-sm text-slate-500 mt-1">A concise water-quality document for property sales. Includes a verification QR code.</p>
              </div>
              <button data-testid="cwc-modal-close" onClick={() => { setOpen(false); setCreated(null); }} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>

            {!created && (
              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Water record share link (optional)</label>
                  <input
                    data-testid="cwc-share-url"
                    type="url"
                    value={shareUrl}
                    onChange={(e) => setShareUrl(e.target.value)}
                    placeholder="https://… active share link to embed"
                    className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm"
                  />
                  <p className="text-[11px] text-slate-500 mt-1.5">If supplied, the cert will reference this link so the conveyancer can drill into the full water record.</p>
                </div>
                <div className="rounded-2xl bg-cyan-50 border border-cyan-100 p-3 text-xs text-cyan-900 leading-relaxed">
                  <strong>Once generated, this certificate is immutable.</strong> A unique certificate number is issued. Anyone can verify its authenticity at <code>/verify/cert/&lt;number&gt;</code>.
                </div>
                <Button data-testid="cwc-generate-btn" onClick={handleGenerate} disabled={generating} className="btn-aqua rounded-full font-semibold w-full">
                  {generating ? "Generating..." : "Generate certificate"}
                </Button>
              </div>
            )}

            {created && (
              <div data-testid="cwc-created" className="mt-6">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-2.5">
                  <FileText className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-emerald-900 flex-1 min-w-0">
                    <div className="font-semibold">Certificate ready</div>
                    <div className="font-mono text-xs mt-1 break-all">{created.cert_number}</div>
                  </div>
                </div>

                <dl className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Property</dt><dd className="text-slate-900 font-medium text-right">{created.snapshot.address}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Water Health Score</dt><dd className="text-slate-900 font-bold">{created.snapshot.health_score}/100</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Tests on record</dt><dd className="text-slate-900 font-medium">{created.snapshot.test_count}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Trend</dt><dd className="text-slate-900 font-medium capitalize">{created.snapshot.trend}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-slate-500">Status</dt><dd className={`font-bold ${created.snapshot.status === "clean" ? "text-emerald-700" : "text-amber-700"}`}>{created.snapshot.status === "clean" ? "No significant issues" : "Issues identified"}</dd></div>
                </dl>

                <div className="mt-5 flex flex-col gap-2.5">
                  <Button data-testid="cwc-download-btn" onClick={() => downloadPdf(created.id, created.cert_number)} className="btn-aqua rounded-full font-semibold w-full">
                    <Download className="w-4 h-4 mr-1.5" /> Download PDF
                  </Button>
                  <a
                    href={created.verification_url}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-white border border-slate-200 hover:border-cyan-500 text-slate-700 hover:text-cyan-700 text-sm font-semibold"
                  >
                    <ExternalLink className="w-4 h-4" /> Open verification page
                  </a>
                </div>

                {/* Phase 2D: share with partner */}
                <SharePartnerSection certId={created.id} certNumber={created.cert_number} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}



/**
 * SharePartnerSection — inline expander to email the certificate to a
 * conveyancer / agent / buyer / property manager with a partner-invitation CTA
 * embedded. Tracks engagement via the /api/r/<token>/... links.
 */
function SharePartnerSection({ certId, certNumber }) {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    recipient_email: "", recipient_name: "", business_name: "",
    partner_type: "conveyancer", custom_message: "", send_email: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [shared, setShared] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const setCheckbox = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const submit = async () => {
    if (!form.recipient_email) { toast.error("Recipient email required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/conveyancing/certificates/share-with-partner", {
        cert_id: certId,
        ...form,
      });
      setShared(data);
      toast.success(form.send_email ? "Certificate emailed" : "Share link created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Share failed");
    } finally { setSubmitting(false); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Copied to clipboard");
  };

  if (shared) {
    return (
      <div data-testid="cwc-share-confirmed" className="mt-5 rounded-2xl bg-cyan-50 border border-cyan-200 p-4">
        <div className="flex items-center gap-2 text-cyan-900 font-semibold text-sm">
          <Send className="w-4 h-4" /> Shared with {shared.partner?.business_name || shared.partner?.email}
        </div>
        <p className="text-xs text-cyan-800 mt-1">We'll track every view, download and verification — visible in the admin Partners dashboard.</p>
        <div className="mt-3 grid grid-cols-1 gap-2 text-xs">
          <button onClick={() => copyLink(shared.cert_url)} className="text-left font-mono bg-white border border-cyan-200 rounded-xl p-2.5 hover:border-cyan-400 break-all">
            <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 not-italic">Tracked cert link · click to copy</span><br />
            {shared.cert_url}
          </button>
          <button onClick={() => copyLink(shared.verification_url)} className="text-left font-mono bg-white border border-cyan-200 rounded-xl p-2.5 hover:border-cyan-400 break-all">
            <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 not-italic">Verification link</span><br />
            {shared.verification_url}
          </button>
        </div>
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        data-testid="cwc-share-toggle"
        onClick={() => setExpanded(true)}
        className="mt-5 w-full inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-cyan-700 text-white text-sm font-semibold transition-colors"
      >
        <Mail className="w-4 h-4" /> Share with conveyancer / agent
      </button>
    );
  }

  return (
    <div data-testid="cwc-share-form" className="mt-5 rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-cyan-700">Share & track</div>
        <button onClick={() => setExpanded(false)} className="text-xs text-slate-500 hover:text-slate-900">cancel</button>
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-700">Send to</Label>
        <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
          <SelectTrigger data-testid="share-partner-type" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{PARTNER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-700">Recipient email</Label>
        <Input data-testid="share-recipient-email" type="email" value={form.recipient_email} onChange={set("recipient_email")} placeholder="jane@conveyancingco.com" className="mt-1" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs font-semibold text-slate-700">Name</Label>
          <Input data-testid="share-recipient-name" value={form.recipient_name} onChange={set("recipient_name")} placeholder="Jane Smith" className="mt-1 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Business</Label>
          <Input data-testid="share-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Jane Smith Conveyancing" className="mt-1 text-sm" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-700">Message (optional)</Label>
        <textarea data-testid="share-message" rows={3} value={form.custom_message} onChange={set("custom_message")} placeholder="Personal note to include in the email." className="mt-1 w-full rounded-xl border-slate-200 text-sm p-3" />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input data-testid="share-send-email" type="checkbox" checked={form.send_email} onChange={setCheckbox("send_email")} className="rounded border-slate-300" />
        Email certificate with partner-invitation CTA
      </label>
      <Button data-testid="share-submit" onClick={submit} disabled={submitting || !form.recipient_email} className="btn-aqua rounded-full font-semibold w-full">
        <Send className="w-4 h-4 mr-1.5" /> {submitting ? "Sharing…" : "Share & track"}
      </Button>
    </div>
  );
}
